<script setup lang="ts">
import { ref, computed, watch } from "vue";
import type { WithTabsProps } from "./WithTabsProps";
import type { PageBlock } from "@/views/blocks/PageBlock.interface";
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import TabsHeader from "../components/TabsHeader.vue";
import TabsContent from "../components/TabsContent.vue";
import Section from "@/views/blocks/shared/section/layout/section/Section.vue";
import FullSpacing from "@/views/blocks/shared/section/layout/spacing/FullSpacing.vue";
import EditableElement from "@/views/page/EditableElement.vue";

const props = defineProps<{
    block: PageBlock<WithTabsProps>;
    pageColors: CssVarColors;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);

const activeTab = ref<number>(dataValues.value.tabs.data.items[0]?.id || 1);

const handleTabClick = (tabId: number) => {
    activeTab.value = tabId;
};

const activeTabContent = computed(() =>
    dataValues.value.tabs.data.items.find((tab) => tab.id === activeTab.value)
);

// Watch for changes in tabs data and reset active tab if current one is deleted
watch(
    () => dataValues.value.tabs.data.items,
    (newTabs) => {
        // Check if the currently active tab still exists
        const currentTabExists = newTabs.some((tab) => tab.id === activeTab.value);

        // If current tab doesn't exist and there are tabs available, set to first tab
        if (!currentTabExists && newTabs.length > 0) {
            activeTab.value = newTabs[0].id;
        }
    },
    { deep: true }
);
</script>

<template>
    <Section :id="block.webId" :backgroundColor="styleValues.background">
        <FullSpacing>
            <div class="max-w-2xl lg:max-w-7xl mx-auto space-y-12">
                <TabsHeader
                    :title="dataValues.title"
                    :description="dataValues.description"
                    :titleTextColor="styleValues.text"
                    :descriptionTextColor="styleValues.text"
                    :blockId="block.id"
                />

                <!-- Tabs Navigation -->
                <div :style="{ borderColor: styleValues.border }" class="border-b">
                    <nav class="flex gap-8 overflow-x-auto whitespace-nowrap -mb-[1px]">
                        <EditableElement
                            v-for="tab in dataValues.tabs.data.items"
                            :key="tab.id"
                            :blockId="block.id"
                            :field-name="`tabs_${tab.id}`"
                            :hideable="false"
                        >
                            <button
                                @click="handleTabClick(tab.id)"
                                class="tab-button pb-4 px-1 transition-colors duration-200 cursor-pointer border-b-[2px] border-transparent"
                                :class="{ active: activeTab === tab.id }"
                            >
                                {{ tab.label }}
                            </button>
                        </EditableElement>
                    </nav>
                </div>

                <TabsContent
                    :activeTabContent="activeTabContent"
                    :tabTitleTextColor="styleValues.text"
                    :tabDescriptionTextColor="styleValues.text"
                    :blockId="block.id"
                    :fieldName="`tabs_${activeTabContent?.id}`"
                    :hideable="false"
                />
            </div>
        </FullSpacing>
    </Section>
</template>

<style scoped>
.tab-button {
    color: color-mix(in srgb, v-bind("styleValues.text") 70%, transparent);
}

.tab-button:focus-visible {
    outline: none;
    border-color: color-mix(in srgb, v-bind("styleValues.text") 50%, transparent);
}

.tab-button.active {
    border-color: v-bind("styleValues.brand");
    color: v-bind("styleValues.brand");
    cursor: default;
}

.tab-button:not(.active):hover {
    border-color: color-mix(in srgb, v-bind("styleValues.text") 70%, transparent);
}
</style>
